import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";

// store
import { accountDataStore, dbDataStore, messageAndErrorStore, userStore } from "../../store/Store";

// hooks
import { useEscape } from "../../hooks/useEscape";

// assets
import Close from "../../assets/close.svg";
import AddCircle from "../../assets/add_circle.svg";
import Trash from "../../assets/delete.svg";

// components
import DropDown from "../../formComponents/DropDown";
import FormButton from "../../formComponents/FormButton";
import { getFormattedDate } from "../../funcs/helperFuncs";

export default function AddAdjustment({
  inv,
  setTrigger,
  projectId,
  isVendor,
  id,
  editAdjustment,
  editAdjustmentFields,
  setAdjustments,
  batchInvoices,
  setBatchChecked,
  setBatchInvoices,
  setEnableCheckBoxes,
}) {
  // store
  const JwtToken = userStore((data) => data.JwtToken);
  const currentCompany = userStore((data) => data.currentCompany);
  const currentUser = userStore((data) => data.currentUser);
  const setAuthStatus = userStore((data) => data.setAuthStatus);
  const companyLists = dbDataStore((data) => data.companyLists);
  const setInvoiceAdjustments = accountDataStore(
    (data) => data.setInvoiceAdjustments
  );
  const toggleNewMessage = messageAndErrorStore(data => data.toggleNewMessage)
  // local
  const navigate = useNavigate();
  const [counter, setCounter] = useState(0);
  const [fieldOptions, setFieldOptions] = useState([]);
  const [listOptions, setListOptions] = useState([]);
  const [list, setList] = useState();
  const [dataPending, setDataPending] = useState(false);
  const [submitPending, setSubmitPending] = useState(false);
  const [adjustment, setAdjustment] = useState({
    id: id,
    invoice_id: inv.id,
    is_vendor: isVendor,
    project_id: projectId,
    company_id: currentCompany.id,
    target_company_id: inv.target_company_id,
    total_adjustment: 0,
    created_by_id: currentUser.id,
    updated_by_id: currentUser.id,
    company_list_id: 0,
  });

  const [adjustmentFields, setAdjustmentFields] = useState([
    {
      id: 0,
      invoice_adjustment_id: 0,
      date_of_adjustment: "",
      company_lists_field_id: 0,
      is_credit: false,
      field_name: "",
      field_value: 0,
      adjustment_comments: "",
      created_by_id: currentUser.id,
      updated_by_id: currentUser.id,
    },
  ]);
  const typeOptions = [
    { value: true, label: "Credit" },
    { value: false, label: "Charge" },
  ];
  // get list options
  useEffect(() => {
    var arr = [];
    if (companyLists) {
      companyLists.forEach((l) => {
        if (l.list_type_id === 35) {
          arr.push({ value: l.id, label: l.list_name });
        }
      });
      setListOptions(arr);
    }
  }, [companyLists]);

  // get adjustment if id is not 0
  useEffect(() => {
    if (editAdjustment && editAdjustmentFields) {
      let arr = [];
      editAdjustmentFields.forEach((af) => {
        arr.push({
          id: af.id,
          invoice_adjustment_id: af.invoice_adjustment_id,
          date_of_adjustment: af.date_of_adjustment.slice(0, 10),
          company_lists_field_id: af.company_lists_field_id,
          is_credit: af.is_credit,
          field_name: af.field_name,
          field_value: af.field_value,
          adjustment_comments: af.adjustment_comments,
          created_by_id: af.created_by_id,
          updated_by_id: currentUser.id,
        });
      });
      setAdjustmentFields(arr);
      setAdjustment(editAdjustment);
      setCounter(editAdjustmentFields.length - 1);
      if (listOptions.length > 0) {
        const l = listOptions.filter(
          (x) => x.value === adjustment.company_list_id
        );
        if (l.length > 0) {
          setList(l[0]);
        }
      }
    }
  }, [
    editAdjustment,
    editAdjustmentFields,
    setAdjustmentFields,
    setAdjustment,
    listOptions,
    adjustment,
    currentUser,
  ]);

  // get field options
  useEffect(() => {
    if (list) {
      setDataPending(true);
      const headers = new Headers();
      headers.append("Content-Type", "application/json");
      headers.append("Authorization", "Bearer " + JwtToken);

      const controller = new AbortController();
      const requestOptions = {
        method: "GET",
        headers,
        signal: controller.signal,
      };

      fetch(
        `${process.env.REACT_APP_BACKEND}/pc/company-list/${list.value}`,
        requestOptions
      )
        .then((res) => {
          if (!res.ok) {
            if (res.status === 401) {
              setAuthStatus(false);
              navigate("/");
              return;
            } else {
              toggleNewMessage("Could not get list fields");
              setDataPending(false);
              return Promise.resolve();
            }
          } else {
            return res.json();
          }
        })
        .then((data) => {
          if (!data) {
            return;
          } else {
            var arr = [];
            if (data.company_list_fields) {
              data.company_list_fields.forEach((f) => {
                arr.push({ value: f.id, label: f.field_name });
              });
            }
            setFieldOptions(arr);
          }
          setDataPending(false);
        })
        .catch((err) => {
          toggleNewMessage("Something Went Wrong");
          console.log(err);
          return;
        })
        .finally(() => setDataPending(false));
      return () => controller.abort();
    }
  }, [
    list,
    navigate,
    setFieldOptions,
    setAuthStatus,
    JwtToken,
    setDataPending,
    toggleNewMessage,
  ]);

  useEscape(setTrigger);

  const handleClose = () => {
    setTrigger(false);
  };

  const handleField = (e, idx) => {
    switch (e.target.name) {
      case "field_value":
        setAdjustmentFields((prevData) => {
          const newData = [...prevData];
          newData[idx] = {
            ...newData[idx],
            [e.target.name]: parseFloat(e.target.value),
          };
          return newData;
        });
        break;
      default:
        setAdjustmentFields((prevData) => {
          const newData = [...prevData];
          newData[idx] = { ...newData[idx], [e.target.name]: e.target.value };
          return newData;
        });
        break;
    }
  };

  const handleSelect = (o, e, idx) => {
    if (e.name === "field_name") {
      setAdjustmentFields((currentData) => {
        const newData = [...currentData];
        newData[idx] = {
          ...newData[idx],
          field_name: o.label,
          company_lists_field_id: o.value,
        };
        return newData;
      });
    } else if (e.name === "is_credit") {
      setAdjustmentFields((currentData) => {
        const newData = [...currentData];
        newData[idx] = { ...newData[idx], is_credit: o.value };
        return newData;
      });
    }
  };

  const handleAdd = () => {
    setCounter(counter + 1);
    setAdjustmentFields((currentData) => {
      const newData = [...currentData];
      newData.push({
        id: 0,
        invoice_adjustment_id: 0,
        date_of_adjustment: "",
        company_lists_field_id: 0,
        is_credit: false,
        field_name: "",
        field_value: 0,
        adjustment_comments: "",
        created_by_id: currentUser.id,
        updated_by_id: currentUser.id,
      });
      return newData;
    });
  };
  const handleDelete = () => {
    setCounter(counter - 1);
    setAdjustmentFields((currentData) => {
      const newData = [...currentData];
      newData.pop();
      return newData;
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitPending(true);
    const reqBody = {
      invoice_adjustment: { ...adjustment },
      adjustment_fields: adjustmentFields.map((x) => ({ ...x })),
    };

    // loop through fields, adjust dates and get total
    var total = 0;

    reqBody.adjustment_fields.forEach((f, idx) => {
      switch (adjustment.is_vendor) {
        case true:
          if (f.is_credit) {
            total = total + parseFloat(f.field_value);
          } else {
            total = total - parseFloat(f.field_value);
          }
        break
        default:
          if (f.is_credit) {
            total = total - parseFloat(f.field_value);
          } else {
            total = total + parseFloat(f.field_value);
          }
      }

      reqBody.adjustment_fields[idx].date_of_adjustment = getFormattedDate(
        f.date_of_adjustment
      );
    });
    reqBody.invoice_adjustment.total_adjustment = total;
    var method = "POST";
    if (id > 0) {
      method = "PATCH";
    }
    reqBody.invoice_adjustment.company_list_id = list.value;
    const headers = new Headers();
    headers.append("Content-Type", "application/json");
    headers.append("Authorization", "Bearer " + JwtToken);
    const controller = new AbortController();
    
    if (batchInvoices) {
      const batchBody = {
        invoices: [],
        invoices_info: [],
        adjustments: [],
        adjustment_values: [],
        action_type: "Add Adjustment",
      };

      batchInvoices.forEach((inv) => {
        var adj = { ...reqBody.invoice_adjustment };
        adj.invoice_id = inv.invoice.id;
        adj.target_company_id = inv.invoice.target_company_id;
        batchBody.adjustments.push(adj);
        batchBody.adjustment_values.push(reqBody.adjustment_fields);
        batchBody.invoices.push(inv.invoice);
        batchBody.invoices_info.push(inv.invoice_info);
      });

      let requestOptions = {
        method: "PATCH",
        headers,
        body: JSON.stringify(batchBody),
        signal: controller.signal,
      };

      fetch(
        `${process.env.REACT_APP_BACKEND}/pc/account-vendor-invoices-batch-update`,
        requestOptions
      )
        .then((res) => {
          if (!res.ok) {
            if (res.status === 401) {
              setAuthStatus(false);
              setSubmitPending(false);
              navigate("/login");
            } else if (res.status === 403) {
              toggleNewMessage("Forbidden: 403");
              setSubmitPending(false);
              return Promise.resolve();
            } else {
              toggleNewMessage("Something Went Wrong");
              setSubmitPending(false);
              return Promise.resolve();
            }
          } else {
            return res.json();
          }
        })
        .then((data) => {
          if (!data) {
            setSubmitPending(false);
            setTrigger(false);
            return;
          } else {
            setInvoiceAdjustments(data.invoice_adjustments);
            setSubmitPending(false);
            toggleNewMessage(null, "Adjustments Added");
            setBatchChecked(false);
            setBatchInvoices([]);
            setEnableCheckBoxes(false)
            setTrigger(false);
            return;
          }
        })
        .catch((err) => {
          console.log(err);
          toggleNewMessage("Something Went Wrong");
          setSubmitPending(false);
          return;
        })
        .finally(() => setSubmitPending(false));
      return () => controller.abort();
    } else {
      let requestOptions = {
        method,
        headers,
        body: JSON.stringify(reqBody),
        signal: controller.signal,
      };

      fetch(
        `${process.env.REACT_APP_BACKEND}/pc/invoice-adjustment/${id}?is_received=false`,
        requestOptions
      )
        .then((res) => {
          if (!res.ok) {
            if (res.status === 401) {
              setAuthStatus(false);
              navigate("/login");
            } else {
              toggleNewMessage("Something Went Wrong");
              setSubmitPending(false);
              return Promise.resolve();
            }
          } else {
            return res.json();
          }
        })
        .then((data) => {
          if (!data) {
            setSubmitPending(false);
            return;
          } else {
            if (id > 0) {
              setAdjustments(data);
            } else {
              setInvoiceAdjustments(data);
            }
            setSubmitPending(false);
            toggleNewMessage(null, "Adjustment Added");
            setTrigger(false);
            return;
          }
        })
        .catch((err) => {
          console.log(err);
          toggleNewMessage("Something Went Wrong");
          setSubmitPending(false);
          return;
        })
        .finally(() => setSubmitPending(false));
      return () => controller.abort();
    }
  };

  return (
    inv && (
      <div className="popup">
        <div className="ratesheet-popup-inner">
          {!submitPending && (
            <div>
              <img
                src={Close}
                alt="close"
                className="close"
                onClick={handleClose}
              />
            </div>
          )}
          <h4>
            {batchInvoices
              ? "Add Ajustments to Selected Invoices"
              : `Add Adjustment to ${inv.invoice_name}`}
          </h4>
          <form onSubmit={handleSubmit}>
            <div className="row justify-content-center">
              {!editAdjustment && (
                <DropDown
                  name="list_name"
                  options={listOptions}
                  onChange={(o) => setList(o)}
                  placeholder="Select List"
                  value={list}
                  required
                  classFour="dropdown-auto"
                />
              )}
            </div>
            {list && !dataPending && fieldOptions.length > 0 && (
              <div className="container-fluid text-center">
                <div className="row">
                  <h5>Adjustments</h5>
                </div>
                <div className="row">
                  <div className="col adjustment-label">Adjustment</div>
                  <div className="col adjustment-label">Value</div>
                  <div className="col adjustment-label">Adjustment Type</div>
                  <div className="col adjustment-label">Adjustment Date</div>
                  <div className="col adjustment-label">Comments</div>
                  <div className="col adjustment-label"></div>
                </div>
                {Array.from(Array(counter + 1)).map((_, idx) => {
                  return (
                    <div className="row" key={idx}>
                      <div className="mileage-rate">
                        <Select
                          name={`field_name`}
                          options={fieldOptions}
                          onChange={(o, e) => handleSelect(o, e, idx)}
                          placeholder={`Field ${idx + 1}`}
                          value={
                            fieldOptions.filter(
                              (x) =>
                                x.label === adjustmentFields[idx].field_name
                            )[0]
                          }
                          // className="dropdown"
                          required
                        />
                      </div>
                      <div className="col mileage-rate">
                        <input
                          name={`field_value`}
                          type="number"
                          step={0.01}
                          value={adjustmentFields[idx].field_value || ""}
                          onChange={(e) => handleField(e, idx)}
                          placeholder="0"
                          required
                        />
                      </div>
                      <div className="col mileage-rate">
                        <Select
                          name={`is_credit`}
                          options={typeOptions}
                          onChange={(o, e) => handleSelect(o, e, idx)}
                          placeholder="Value Type"
                          value={typeOptions.filter(
                            (x) => x.value === adjustmentFields[idx].is_credit
                          )}
                          required
                        />
                      </div>
                      <div className="col mileage-rate">
                        <input
                          name={`date_of_adjustment`}
                          type="date"
                          value={adjustmentFields[idx].date_of_adjustment}
                          onChange={(e) => handleField(e, idx)}
                          required
                        />
                      </div>
                      <div className="col mileage-rate">
                        <input
                          name={`adjustment_comments`}
                          type="text"
                          value={adjustmentFields[idx].adjustment_comments}
                          onChange={(e) => handleField(e, idx)}
                        />
                      </div>
                      <div className="col d-flex">
                        {idx === counter && (
                          <div className="mileage">
                            <img
                              id={idx}
                              className="delete-band"
                              alt="delete_img"
                              src={AddCircle}
                              onClick={handleAdd}
                            />
                          </div>
                        )}
                        {idx === counter && idx !== 0 && (
                          <div className="mileage">
                            <img
                              id={idx}
                              className="delete-band"
                              alt="delete_img"
                              src={Trash}
                              onClick={handleDelete}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
                <FormButton pending={submitPending} name={"Submit"} />
              </div>
            )}
          </form>
        </div>
      </div>
    )
  );
}
